import React, {
  FC,
  SelectHTMLAttributes,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useRef,
  useMemo,
} from 'react';
import { Listbox, Transition } from '@headlessui/react';
import {
  CaretDown,
  MultiSelectIconBlank,
  MultiSelectIconFilled,
  MultiSelectDisabledIcon,
} from '..';
import ErrorMessage from '../error-message/error-message';

type OptionType = {
  name: string;
  value: string;
};

export interface MultiSelectProps
  extends SelectHTMLAttributes<HTMLSelectElement> {
  selectLabel: string;
  options: OptionType[];
  width?: 'full' | 'lg' | 'md';
  positioning?: 'relative' | 'absolute';
  selectedOptions?: OptionType[];
  setSelectedOptions?: Dispatch<SetStateAction<OptionType[]>>;
  onChange?: any;
  errors?: any;
  name?: string;
  withLabel?: boolean;
  isValid?: boolean;
  errorMessage?: string;
  largeHeight?: boolean;
  disabled?: boolean;
  maximum?: number;
}

export const MultiSelect: FC<MultiSelectProps> = ({
  className,
  selectLabel,
  options = [],
  positioning,
  selectedOptions = [],
  setSelectedOptions,
  width,
  errors,
  name,
  withLabel,
  isValid = true,
  errorMessage,
  largeHeight,
  disabled,
  maximum,
}: MultiSelectProps) => {
  const ref = useRef<any>();
  const [selectedList, setSelectedList] =
    useState<OptionType[]>(selectedOptions);
  const [listOptions, setListOptions] = useState(options);
  const [defaultVal, setDefaultVal] = useState<string | undefined>('');
  const [label, setLabel] = useState<string>(selectLabel);
  const [visible, setVisible] = useState<boolean>(false);
  const [isFocused, setFocused] = useState<boolean>(false);

  const handleOnclick = useMemo(
    () =>
      (item: OptionType, e: React.SyntheticEvent<EventTarget>): void => {
        e.preventDefault();
        const doesContainOption = selectedList.some(
          (option) => option.value === item.value
        );

        const isAll = item.name.toLowerCase() === 'all';

        const itemAdded = isAll ? [...options] : [...selectedList, item];

        const itemRemoved = !isAll
          ? selectedList.filter(
              (option) =>
                option.value !== item.value &&
                option.name.toLowerCase() !== 'all'
            )
          : [];

        const newList = doesContainOption ? itemRemoved : itemAdded;
        setSelectedList(newList);
        setDefaultVal(doesContainOption ? defaultVal : '');
      },
    [selectedList]
  );

  const handleListChange = (item: any) => {
    if (!disabled) {
      const doesContainOption = selectedList.some(
        (option) => option.value === item.value
      );
      const isAll = item.name.toLowerCase() === 'all';
      let itemAdded;

      if (maximum) {
        if (selectedList.length < maximum) {
          itemAdded = isAll ? [...options] : [...selectedList, item];
        } else {
          itemAdded = [...selectedList];
        }
      } else {
        itemAdded = isAll ? [...options] : [...selectedList, item];
      }

      const itemRemoved = !isAll
        ? selectedList.filter(
            (option) =>
              option.value !== item.value && option.name.toLowerCase() !== 'all'
          )
        : [];
      const newList = doesContainOption ? itemRemoved : itemAdded;
      setSelectedList(newList);
      setSelectedOptions && setSelectedOptions(newList);
      setDefaultVal(doesContainOption ? defaultVal : '');
    }
  };

  useEffect(() => {
    const handleMouseDownClose = (e: MouseEvent) => {
      if (visible && ref.current && !ref.current.contains(e.target)) {
        setVisible(false);
      }
    };

    const handleKeyDownClose = (e: KeyboardEvent) => {
      if (
        (visible && e.key === 'Escape') ||
        (visible && e.key === 'Backspace')
      ) {
        setVisible(false);
      }
    };

    if (selectedList.length === 0) {
      setDefaultVal(selectLabel);
    }

    setSelectedOptions && setSelectedOptions(selectedList);

    document.addEventListener('keydown', handleKeyDownClose);
    document.addEventListener('mousedown', handleMouseDownClose);

    return () => {
      document.removeEventListener('keydown', handleKeyDownClose);
      document.removeEventListener('mousedown', handleMouseDownClose);
    };
  }, [selectedList, visible]);

  useEffect(() => {
    setSelectedList && setSelectedList(selectedOptions);
  }, [selectedOptions?.length]);

  useEffect(() => {
    setListOptions(options);
  }, [options?.length]);

  useEffect(() => {
    const handleKeyOpen = (e: KeyboardEvent) => {
      if (isFocused && e.key === 'Enter') {
        e.preventDefault();
        setVisible(true);
      }
    };
    document.addEventListener('keydown', handleKeyOpen);

    return () => {
      document.removeEventListener('keydown', handleKeyOpen);
    };
  }, [isFocused]);

  useEffect(() => setLabel(selectLabel), [selectLabel]);

  return (
    <div
      ref={ref}
      className={`relative ${
        width === 'full'
          ? 'w-full'
          : width === 'lg'
          ? 'w-1/2'
          : width === 'md'
          ? 'w-52'
          : 'w-44'
      } ${className ? className : ''}`}
    >
      <Listbox
        as="div"
        value={selectedList}
        className={`text-left cursor-pointer`}
        onChange={(value: any) => {
          handleListChange(value);
        }}
        aria-label="Multi-Select"
      >
        <>
          <Listbox.Button
            as="div"
            className={`w-full peer relative  ${
              visible
                ? 'border-b-2 border-thryv-black-500'
                : 'border-b text-thryv-steel border-thryv-gray-light-500'
            } transition-all`}
            onMouseDown={() => setVisible(!visible)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            tabIndex={0}
            data-testid="multiselect"
            role="button"
          >
            <div>
              <span
                className={`absolute ${
                  visible || withLabel
                    ? '-top-5 opacity-100'
                    : 'top-0 opacity-0'
                } left-0 ${
                  errors && name && errors[name] && errors[name].message
                    ? 'text-utility-error-danger'
                    : 'text-thryv-steel'
                } text-label-text mb-2 transition-all duration-400`}
              >
                {label}
              </span>
              <div className="mt-2 flex justify-between items-center">
                <div
                  className="w-5/6 truncate"
                  data-testid="multiselect-option"
                >
                  {!visible ? (
                    defaultVal!.length > 0 ? (
                      <>
                        {selectedList.length === 0 ? (
                          <span className="text-thryv-steel text-form-text mb-5 transition-all">
                            {defaultVal}
                          </span>
                        ) : (
                          selectedList.slice(0, 3).map((option, i) => (
                            <span
                              key={i}
                              className="text-thryv-black-500 text-form-text mb-2 transition-all capitalize"
                            >
                              {option.name}
                              {i === selectedList.length - 1 ? '' : ', '}
                              {i === 2 && selectedList.length > 3
                                ? ` and ${selectedList.length - 3} more`
                                : ''}
                            </span>
                          ))
                        )}
                      </>
                    ) : selectedList.length > 0 ? (
                      selectedList.slice(0, 3).map((option, i) => (
                        <span
                          key={i}
                          className="text-thryv-black-500 text-form-text mb-2 transition-all capitalize"
                        >
                          {option.name}
                          {i === selectedList.length - 1 ? '' : ', '}
                          {i === 2 && selectedList.length > 3
                            ? ` and ${selectedList.length - 3} more`
                            : ''}
                        </span>
                      ))
                    ) : null
                  ) : (
                    <span className="text-thryv-black-500 text-form-text mb-2 transition-all">
                      -- Select --
                    </span>
                  )}
                </div>
                <CaretDown
                  className={`fill-current ml-6 ${
                    visible ? 'text-thryv-black-500' : ''
                  }`}
                />
              </div>
            </div>
          </Listbox.Button>

          <Transition
            as="div"
            show={visible}
            enter="transition ease-in duration-300 relative z-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-out duration-200"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Listbox.Options
              className={`overflow-auto ${
                largeHeight ? 'h-64' : 'h-32'
              } w-full mt-3 origin-top-left bg-white rounded shadow-thryv focus:outline-none ${
                positioning || 'absolute'
              } z-100`}
              static
            >
              <div>
                {listOptions.map((option, i) => (
                  <div key={i}>
                    <Listbox.Option value={option}>
                      {({ active }) =>
                        disabled ? (
                          <button
                            className={` flex justify-start items-center ${
                              active &&
                              'bg-thryv-white-300 transition-all duration-300'
                            } group flex items-center w-full px-2 py-2 text-[12px] font-open-sans capitalize transition-all`}
                            type="button"
                            tabIndex={0}
                          >
                            {selectedList.some(
                              (current) => current.value === option.value
                            ) ? (
                              <MultiSelectDisabledIcon />
                            ) : (
                              <MultiSelectIconBlank />
                            )}
                            <span className="ml-1">{option.name}</span>
                          </button>
                        ) : (
                          <button
                            className={` flex justify-start items-center ${
                              active &&
                              'bg-thryv-white-300 transition-all duration-300'
                            } group flex items-center w-full px-2 py-2 text-[12px] font-open-sans capitalize transition-all`}
                            onClick={(e) => handleOnclick(option, e)}
                            tabIndex={0}
                            type="button"
                          >
                            {selectedList.some(
                              (current) => current.value === option.value
                            ) ? (
                              <MultiSelectIconFilled />
                            ) : (
                              <MultiSelectIconBlank />
                            )}
                            <span className="ml-1">{option.name}</span>
                          </button>
                        )
                      }
                    </Listbox.Option>
                  </div>
                ))}
              </div>
            </Listbox.Options>
          </Transition>
        </>
      </Listbox>
      {errors && name && errors[name] && errors[name].message && (
        <ErrorMessage errorMessage={errors[name].message} />
      )}
      {!isValid && errorMessage && <ErrorMessage errorMessage={errorMessage} />}
    </div>
  );
};
